import { DTCMBridge } from '../bridge/client-bridge-defs.ts';
import {emitAdImpressionEvent, emitAdMonetizationEvent} from './ads-util.ts';

export const MONETIZATION_MOMENT_OUTBRAIN = 0.01787677385;
export const MONETIZATION_MOMENT_WVIEW_EN_TOP = 0.00017;
export const MONETIZATION_MOMENT_WVIEW_EN_BOTTOM = 0.00026;
export const MONETIZATION_MOMENT_WVIEW_EN_ADHESION = 0.00013;

class AdImpressionCollector {
  /**
   * Emit an ad impression event
   * @param bridge - The bridge instance to emit through
   * @param slotId - The ad slot ID that triggered the impression (for logging)
   * @param source - Optional source identifier for debugging (e.g., 'AdInitLayout', 'Lookbook')
   */
  public emitAdImpression(bridge: DTCMBridge, slotId: string, source?: string): boolean {
    if (!slotId || !bridge) {
      console.warn('AdImpressionCollector: Invalid slotId or bridge provided');
      return false;
    }

    // Emit the event
    console.debug(`AdImpressionCollector: Emitting ad impression for slot ${slotId} from ${source || 'unknown'}`);
    emitAdImpressionEvent(bridge);
    
    return true;
  }

  private calculateMonetizationValueFromAdUnitPath = (adUnitPath: string): number => {
    if (!adUnitPath) {
      return 0;
    }
    // static logic of assigning value:
    switch (adUnitPath) {
      case '/180049092/ROS_OM_DTMOMENT_WVIEW_EN_ADHESION' : return MONETIZATION_MOMENT_WVIEW_EN_ADHESION;
      case '/180049092/ROS_OM_DTMOMENT_WVIEW_EN_BOTTOM' : return MONETIZATION_MOMENT_WVIEW_EN_BOTTOM;
      case '/180049092/ROS_OM_DTMOMENT_WVIEW_EN_TOP' : return MONETIZATION_MOMENT_WVIEW_EN_TOP;
      default: return 0;
    }
  }

  private calculateMonetizationValueFromWidgetId = (widgetId: string): number => {
    // since we are only looking at moment
    return MONETIZATION_MOMENT_OUTBRAIN;
  }

  public emitAdMonetizationCPM(bridge: DTCMBridge, adUnitPath: string, source?: string): boolean {
    if (!adUnitPath || !bridge) {
      console.warn('AdImpressionCollector: Invalid adUnitPath or bridge provided');
      return false;
    }
    // Emit the event
    const adValue = this.calculateMonetizationValueFromAdUnitPath(adUnitPath);
    console.debug(`AdImpressionCollector: Emitting monetization event for slot ${adUnitPath} from ${source || 'unknown'} with value ${adValue}`);
    if (adValue === 0) {
      console.warn(`AdImpressionCollector: Zero value found, not emitting event for ${adUnitPath}`);
      return false;
    }
    emitAdMonetizationEvent(bridge, {
      source: 'gam',
      type: 'cpm',
      value: adValue
    });
    return true;
  }

  public emitAdMonetizationCTR(bridge: DTCMBridge, widgetId: string, source?: string): boolean {
    if (!widgetId || !bridge) {
      console.warn('AdImpressionCollector: Invalid widgetId or bridge provided');
      return false;
    }
    // Emit the event
    const adValue = this.calculateMonetizationValueFromWidgetId(widgetId);
    console.debug(`AdImpressionCollector: Emitting monetization event for slot ${widgetId} from ${source || 'unknown'} with value ${adValue}`);
    emitAdMonetizationEvent(bridge, {
      source: 'outbrain',
      type: 'cpc',
      value: adValue
    });
    return true;
  }

  /**
   * Clear all tracked emissions (useful for testing or page navigation)
   * Note: This is kept for backwards compatibility but no longer tracks anything
   */
  public clearAll(): void {
    // No longer tracking anything, but keeping for backwards compatibility
    console.debug('AdImpressionCollector: clearAll called (no longer needed)');
  }

  /**
   * Get statistics about tracked emissions
   * Note: This is kept for backwards compatibility but no longer tracks anything
   */
  public getStats(): { totalSlots: number; slotsEmitted: number } {
    // No longer tracking anything, but keeping for backwards compatibility
    return {
      totalSlots: 0,
      slotsEmitted: 0,
    };
  }
}

// Export a singleton instance
export const adImpressionCollector = new AdImpressionCollector();

// Export the class for testing purposes
export { AdImpressionCollector }; 
